import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
} from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  ActivatedRoute,
  ParamMap,
  Params,
  Router,
  convertToParamMap,
} from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import * as Highcharts from 'highcharts'
import 'highcharts/modules/accessibility'
import { HighchartsChartModule } from 'highcharts-angular'
import {
  Subject,
  debounceTime,
  forkJoin,
  switchMap,
  take,
  throwError,
} from 'rxjs'

import { AuthService } from '../../../services/auth.service'
import { hasManagementRole } from '../../../guards/kpi-dashboard-access'
import {
  KpiDashboardDefinition,
  KpiDashboardMember,
  KpiDashboardMembers,
  KpiDashboardService,
} from '../../../services/kpi-dashboard/kpi-dashboard.service'
import { UserStore } from '../../../stores/user.store'
import { captureException } from '../../../util/captureException'
import { handleDashboardNotFound } from '../handle-dashboard-not-found'
import {
  AchievementFilter,
  CoachingFilter,
  MetricFilterLogic,
  OperatorListFilter,
  OperatorListSort,
  OperatorMetricCellViewModel,
  OperatorSortField,
  UNSET_ROLE_KEY,
  aggregateMembers,
  buildMetricCellViewModels,
  buildOrgTargetNote,
  countPeriodMonths,
  filterMembers,
  formatScore,
  getMemberRoleKey,
  isFilterApplied,
  responseStatusDisplay,
  sortMembers,
} from './operator-list.logic'
import {
  DEFAULT_OPERATOR_SCORE_COUNT,
  OPERATOR_LIST_QUERY_KEYS,
  OperatorListQuery,
  areOperatorQueryParamsEqual,
  areOperatorQueryParamValuesEqual,
  buildOperatorListQueryParams,
  parseOperatorListQueryParams,
  validateOperatorListQuery,
  validatePeriodRange,
} from './operator-list-query'

interface RoleOption {
  key: string
  label: string
}

interface OperatorRowViewModel {
  member: KpiDashboardMember
  responseStatusLabel: '未完了' | '完了' | 'シートなし'
  responseStatusClass: 'warning' | 'answered' | 'none'
  roleLabel: string
  metricCells: OperatorMetricCellViewModel[]
  latestScoreText: string
  sparklineOptions: Highcharts.Options | null
  exclusionPending: boolean
}

interface OperatorSummaryViewModel {
  orgTargetNote: string | null
  metricCells: OperatorMetricCellViewModel[]
  averageScoreText: string
}

interface SparklineCacheEntry {
  scores: readonly number[]
  options: Highcharts.Options
}

@UntilDestroy()
@Component({
  selector: 'app-kpi-operator-list',
  standalone: true,
  imports: [
    FormsModule,
    HighchartsChartModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
  ],
  templateUrl: './kpi-operator-list.component.html',
  styleUrl: './kpi-operator-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiOperatorListComponent implements OnInit {
  readonly Highcharts = Highcharts
  readonly scoreCountOptions = [1, 3, DEFAULT_OPERATOR_SCORE_COUNT, 10]
  readonly months = Array.from({ length: 12 }, (_, index) => index + 1)
  readonly nameControl = new FormControl('', { nonNullable: true })

  dashboard: KpiDashboardDefinition | null = null
  membersResult: KpiDashboardMembers | null = null
  displayedRows: OperatorRowViewModel[] = []
  summary: OperatorSummaryViewModel | null = null
  totalMemberCount = 0
  filtersApplied = false
  roleOptions: RoleOption[] = []
  selectedRoleKeys: string[] = []
  metricStatuses: Record<string, AchievementFilter> = {}
  metricLogic: MetricFilterLogic = 'and'
  coachingFilter: CoachingFilter = 'all'
  notAnsweredOnly = false
  reportToUserId: string | null = null
  reportToOptions: KpiDashboardMembers['reportToOptions'] = []
  sort: OperatorListSort = {
    field: 'name',
    direction: 'asc',
    itemId: null,
  }

  private readonly defaultPeriod = currentPeriod()
  periodFrom = this.defaultPeriod
  periodTo = this.defaultPeriod
  scoreCount = DEFAULT_OPERATOR_SCORE_COUNT
  appliedPeriodFrom = this.periodFrom
  appliedPeriodTo = this.periodTo
  appliedScoreCount = this.scoreCount
  // URL には取得完了を待たず、最後に検証を通った期間を使う。
  private validPeriodFrom = this.periodFrom
  private validPeriodTo = this.periodTo
  displayedMonthYear = new Date().getFullYear()
  draftPeriodFrom = this.periodFrom
  draftPeriodTo: string | null = this.periodTo
  selectingPeriodEnd = false
  isMonthRangeOpen = false
  isRoleFilterOpen = false
  canOpenSettings = false
  isLoading = true
  loadErrorMessage = ''
  actionErrorMessage = ''
  periodValidationMessage = ''
  pendingExclusionUserIds = new Set<string>()

  private dashboardId!: string
  private activeLoadId = 0
  private allRolesRequested = true
  private queryName = ''
  private desiredQuery: OperatorListQuery | null = null
  private restoreNameFromUrl = false
  private restoreRolesFromUrl = false
  private requestedQueryParams: ParamMap | null = null
  // 未通知の自画面の更新も含む最新条件。同条件への重複遷移を防ぐ。
  private lastQueryParams = convertToParamMap({})
  // 実際に通知された URL。連続操作でも、まだ URL に反映されていないキーを引き継ぐ。
  private observedQueryParams = convertToParamMap({})
  private readonly reloadRequest = new Subject<boolean>()
  private readonly sparklineCache = new Map<string, SparklineCacheEntry>()
  private pendingExclusionValues = new Map<string, boolean>()
  private settledExclusionValuesDuringLoad = new Map<string, boolean>()

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private userStore: UserStore,
    private kpiDashboardService: KpiDashboardService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.nameControl.valueChanges
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe((name) => {
        // URL 復元前に予約された入力イベントは、復元した入力欄を巻き戻さない。
        if (name !== this.nameControl.value || name === this.queryName) {
          return
        }
        this.queryName = name
        this.onFiltersChanged()
        this.changeDetectorRef.markForCheck()
      })
    this.reloadRequest
      .pipe(debounceTime(300), untilDestroyed(this))
      .subscribe((reload) => {
        if (reload) this.loadMembers()
      })

    const dashboardId = this.route.snapshot.paramMap.get('dashboardId')
    if (dashboardId == null || dashboardId === '') {
      this.isLoading = false
      this.loadErrorMessage = 'ボード ID を取得できませんでした。'
      captureException(new Error('dashboardId is missing'))
      return
    }

    this.dashboardId = dashboardId
    this.observeQueryParams()
    this.loadMembers()
  }

  onPeriodChanged(): void {
    this.actionErrorMessage = ''
    if (!this.validatePeriodRange()) {
      this.changeDetectorRef.markForCheck()
      return
    }

    this.updateQueryParams()
    this.reloadRequest.next(true)
  }

  onScoreCountChanged(): void {
    this.actionErrorMessage = ''
    this.updateQueryParams()
    this.reloadRequest.next(true)
  }

  onFiltersChanged(): void {
    this.actionErrorMessage = ''
    this.recalculateView()
    this.updateQueryParams()
  }

  /** ネイティブ select の空文字と未選択の null を相互変換する。 */
  get reportToSelectValue(): string {
    return this.reportToUserId == null ? '' : this.reportToUserId
  }

  /** select の選択値をコンポーネントの状態と URL に反映する。 */
  set reportToSelectValue(value: string) {
    this.onReportToChanged(value === '' ? null : value)
  }

  /** 上司の選択状態を確定してから URL に反映する。 */
  onReportToChanged(userId: string | null): void {
    const validUserId = this.reportToOptions.some(
      (option) => option.userId === userId,
    )
      ? userId
      : null
    if (this.reportToUserId === validUserId) return
    this.reportToUserId = validUserId
    this.onFiltersChanged()
  }

  /** URL 通知を記録し、自画面の更新以外を初回取得前または取得完了後に復元する。 */
  private observeQueryParams(): void {
    this.route.queryParamMap.pipe(untilDestroyed(this)).subscribe((params) => {
      this.observedQueryParams = params
      if (
        (this.requestedQueryParams != null &&
          areOperatorQueryParamsEqual(params, this.requestedQueryParams)) ||
        (this.activeLoadId > 0 &&
          areOperatorQueryParamsEqual(params, this.lastQueryParams))
      ) {
        return
      }

      this.requestedQueryParams = null
      this.lastQueryParams = params
      this.desiredQuery = parseOperatorListQueryParams(
        params,
        this.defaultPeriod,
        this.scoreCountOptions,
      )
      this.restoreNameFromUrl = true
      this.restoreRolesFromUrl = true
      this.reloadRequest.next(false)
      if (this.activeLoadId === 0) {
        this.restoreAcquisitionConditions(this.desiredQuery)
      } else if (!this.isLoading) {
        const reload = this.restoreQuery()
        this.actionErrorMessage = ''
        this.recalculateView()
        if (reload) this.loadMembers()
        this.changeDetectorRef.markForCheck()
      }
    })
  }

  /** 初回リクエストにも使う期間・回数を復元し、変更の有無を返す。 */
  private restoreAcquisitionConditions(query: OperatorListQuery): boolean {
    const changed =
      this.periodFrom !== query.periodFrom ||
      this.periodTo !== query.periodTo ||
      this.scoreCount !== query.scoreCount
    this.periodFrom = query.periodFrom
    this.periodTo = query.periodTo
    this.scoreCount = query.scoreCount
    if (changed || !this.isMonthRangeOpen) {
      this.draftPeriodFrom = this.periodFrom
      this.draftPeriodTo = this.periodTo
    }
    if (changed && this.isMonthRangeOpen) {
      this.selectingPeriodEnd = false
      this.displayedMonthYear = parsePeriod(this.periodFrom).year
    }
    this.validatePeriodRange()
    return changed
  }

  /** 取得条件を復元し、再取得が不要な場合だけ選択肢で検証して画面と URL を正規化する。 */
  private restoreQuery(): boolean {
    if (this.desiredQuery == null) throw new Error('Requested query is missing')
    const reload = this.restoreAcquisitionConditions(this.desiredQuery)
    if (reload || this.dashboard == null || this.membersResult == null) {
      this.updateQueryParams(this.desiredQuery)
      return reload
    }
    const query = validateOperatorListQuery(this.desiredQuery, {
      roleKeys: this.roleOptions.map((option) => option.key),
      itemIds: this.dashboard.items.map((item) => item.id),
      reportToUserIds: this.reportToOptions.map((option) => option.userId),
      resetMissingRolesToAll: this.restoreRolesFromUrl,
    })
    this.restoreRolesFromUrl = false
    const { filter } = query
    this.queryName = filter.name
    if (this.restoreNameFromUrl) {
      this.nameControl.setValue(filter.name, { emitEvent: false })
      this.restoreNameFromUrl = false
    }
    this.allRolesRequested = filter.roleKeys == null
    this.selectedRoleKeys =
      filter.roleKeys == null
        ? this.roleOptions.map((option) => option.key)
        : [...filter.roleKeys]
    this.reportToUserId = filter.reportToUserId
    this.coachingFilter = filter.coaching
    this.notAnsweredOnly = filter.response === 'notAnswered'
    this.metricStatuses = { ...filter.metricStatuses }
    this.metricLogic = filter.metricLogic
    this.sort = query.sort
    this.updateQueryParams()
    return reload
  }

  /** 現在の絞り込み条件と検証済み期間を URL・詳細遷移用に取り出す。 */
  private currentQuery(): OperatorListQuery {
    return {
      filter: this.currentFilter(),
      sort: this.sort,
      periodFrom: this.validPeriodFrom,
      periodTo: this.validPeriodTo,
      scoreCount: this.scoreCount,
    }
  }

  /** 引数省略時は画面の状態を使い、変更したキーだけを URL にマージする。同じ条件では遷移しない。 */
  private updateQueryParams(query?: OperatorListQuery): void {
    if (query == null) {
      query = this.currentQuery()
      // 画面の状態で上書きしたら、URL 由来としての役職の扱いは終了する。
      this.restoreRolesFromUrl = false
    }
    this.desiredQuery = query
    const params = buildOperatorListQueryParams(query, this.defaultPeriod)
    const normalized = convertToParamMap(params)
    if (areOperatorQueryParamsEqual(normalized, this.lastQueryParams)) return
    const changes: Params = {}
    for (const key of Object.values(OPERATOR_LIST_QUERY_KEYS)) {
      if (
        !areOperatorQueryParamValuesEqual(
          normalized,
          this.observedQueryParams,
          key,
        ) ||
        !areOperatorQueryParamValuesEqual(normalized, this.lastQueryParams, key)
      ) {
        changes[key] = normalized.has(key) ? params[key] : null
      }
    }
    this.requestedQueryParams = normalized
    this.lastQueryParams = normalized
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: changes,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  selectMetricLogic(logic: MetricFilterLogic): void {
    if (this.metricLogic === logic) {
      return
    }

    this.metricLogic = logic
    this.onFiltersChanged()
  }

  onRoleSelectionChanged(roleKeys: string[]): void {
    this.actionErrorMessage = ''
    this.selectedRoleKeys = roleKeys
    this.allRolesRequested =
      roleKeys.length > 0 &&
      this.roleOptions.every((option) => roleKeys.includes(option.key))
    this.onFiltersChanged()
  }

  onAllRoleSelectionChanged(checked: boolean): void {
    this.selectedRoleKeys = checked
      ? this.roleOptions.map((option) => option.key)
      : []
    this.allRolesRequested = checked
    this.onFiltersChanged()
  }

  onRoleOptionChanged(roleKey: string, checked: boolean): void {
    const selectedRoleKeys = new Set(this.selectedRoleKeys)
    if (checked) {
      selectedRoleKeys.add(roleKey)
    } else {
      selectedRoleKeys.delete(roleKey)
    }

    this.onRoleSelectionChanged(
      this.roleOptions
        .filter((option) => selectedRoleKeys.has(option.key))
        .map((option) => option.key),
    )
  }

  toggleMonthRangePicker(): void {
    if (this.isMonthRangeOpen) {
      this.isMonthRangeOpen = false
      return
    }

    this.isRoleFilterOpen = false
    this.displayedMonthYear = parsePeriod(this.periodFrom).year
    this.draftPeriodFrom = this.periodFrom
    this.draftPeriodTo = this.periodTo
    this.selectingPeriodEnd = false
    this.isMonthRangeOpen = true
  }

  toggleRoleFilter(): void {
    const willOpen = !this.isRoleFilterOpen
    this.isMonthRangeOpen = false
    this.isRoleFilterOpen = willOpen
  }

  changeDisplayedMonthYear(offset: number): void {
    this.displayedMonthYear += offset
  }

  selectMonth(month: number): void {
    const selectedPeriod = periodFromYearAndMonth(
      this.displayedMonthYear,
      month,
    )
    if (!this.selectingPeriodEnd) {
      this.draftPeriodFrom = selectedPeriod
      this.draftPeriodTo = null
      this.selectingPeriodEnd = true
      return
    }

    if (selectedPeriod < this.draftPeriodFrom) {
      this.draftPeriodFrom = selectedPeriod
      this.draftPeriodTo = null
      return
    }

    this.draftPeriodTo = selectedPeriod
    this.periodFrom = this.draftPeriodFrom
    this.periodTo = selectedPeriod
    this.selectingPeriodEnd = false
    this.isMonthRangeOpen = false
    this.onPeriodChanged()
  }

  periodSelectionLabel(): string {
    return `${formatPeriod(this.appliedPeriodFrom)}〜${formatPeriod(this.appliedPeriodTo)}`
  }

  monthOptionLabel(month: number): string {
    return formatPeriod(periodFromYearAndMonth(this.displayedMonthYear, month))
  }

  isMonthInRange(month: number): boolean {
    const period = periodFromYearAndMonth(this.displayedMonthYear, month)
    const rangeEnd =
      this.draftPeriodTo == null ? this.draftPeriodFrom : this.draftPeriodTo
    return period >= this.draftPeriodFrom && period <= rangeEnd
  }

  isMonthStart(month: number): boolean {
    return (
      periodFromYearAndMonth(this.displayedMonthYear, month) ===
      this.draftPeriodFrom
    )
  }

  isMonthEnd(month: number): boolean {
    return (
      this.draftPeriodTo != null &&
      periodFromYearAndMonth(this.displayedMonthYear, month) ===
        this.draftPeriodTo
    )
  }

  isRoleSelected(roleKey: string): boolean {
    return this.selectedRoleKeys.includes(roleKey)
  }

  allRolesSelected(): boolean {
    return (
      this.allRolesRequested &&
      this.selectedRoleKeys.length === this.roleOptions.length &&
      this.roleOptions.every((option) =>
        this.selectedRoleKeys.includes(option.key),
      )
    )
  }

  someRolesSelected(): boolean {
    return this.selectedRoleKeys.length > 0 && !this.allRolesSelected()
  }

  @HostListener('document:click', ['$event'])
  closePopoversOnOutsideClick(event: MouseEvent): void {
    const target = event.target
    if (!(target instanceof Element)) {
      this.closePopovers()
      return
    }

    if (target.closest('#month-range-picker') == null) {
      this.isMonthRangeOpen = false
    }
    if (target.closest('#role-filter-dropdown') == null) {
      this.isRoleFilterOpen = false
    }
  }

  @HostListener('document:keydown.escape')
  closePopovers(): void {
    this.isMonthRangeOpen = false
    this.isRoleFilterOpen = false
  }

  onMetricStatusChanged(itemId: string, status: AchievementFilter): void {
    this.actionErrorMessage = ''
    this.metricStatuses = {
      ...this.metricStatuses,
      [itemId]: status,
    }
    this.onFiltersChanged()
  }

  onSort(field: OperatorSortField, itemId: string | null): void {
    const isSameColumn =
      this.sort.field === field && this.sort.itemId === itemId
    this.sort = {
      field,
      itemId,
      direction: isSameColumn && this.sort.direction === 'asc' ? 'desc' : 'asc',
    }
    this.onFiltersChanged()
  }

  retry(): void {
    this.actionErrorMessage = ''
    this.loadMembers()
  }

  openSettings(): void {
    if (!this.canOpenSettings) {
      return
    }

    void this.router.navigate(['/kpi-dashboards', this.dashboardId, 'settings'])
  }

  openOperator(userId: string): void {
    void this.router.navigate(
      ['/kpi-dashboards', this.dashboardId, 'operators', userId],
      {
        queryParams: buildOperatorListQueryParams(
          this.currentQuery(),
          this.defaultPeriod,
        ),
      },
    )
  }

  onRowKeydown(event: KeyboardEvent, userId: string): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    this.openOperator(userId)
  }

  stopRowClick(event: Event): void {
    event.stopPropagation()
  }

  onExclusionChanged(
    member: KpiDashboardMember,
    event: MatCheckboxChange,
  ): void {
    const excluded = event.checked
    const previousValue = member.excludedFromCoaching
    this.actionErrorMessage = ''
    this.pendingExclusionUserIds = new Set([
      ...this.pendingExclusionUserIds,
      member.userId,
    ])
    this.pendingExclusionValues = new Map(this.pendingExclusionValues).set(
      member.userId,
      excluded,
    )
    this.updateMemberExclusion(member.userId, excluded)
    this.changeDetectorRef.markForCheck()

    this.kpiDashboardService
      .setCoachingExclusion(this.dashboardId, member.userId, excluded)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (updated) => {
          if (!updated) {
            this.handleExclusionError(
              member,
              previousValue,
              event,
              new Error('setKpiCoachingExclusion returned false'),
            )
            return
          }

          this.pendingExclusionUserIds = withoutUserId(
            this.pendingExclusionUserIds,
            member.userId,
          )
          this.pendingExclusionValues = withoutMapKey(
            this.pendingExclusionValues,
            member.userId,
          )
          if (this.isLoading) {
            this.settledExclusionValuesDuringLoad = new Map(
              this.settledExclusionValuesDuringLoad,
            ).set(member.userId, excluded)
          }
          this.updateMemberExclusion(member.userId, excluded)
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          this.handleExclusionError(member, previousValue, event, error)
        },
      })
  }

  roleSelectionLabel(): string {
    if (this.allRolesSelected()) {
      return 'すべての役職'
    }
    if (this.selectedRoleKeys.length === 0) {
      return '役職: 0件選択'
    }

    const selectedLabels = this.roleOptions
      .filter((option) => this.selectedRoleKeys.includes(option.key))
      .map((option) => option.label)
    if (selectedLabels.length !== this.selectedRoleKeys.length) {
      throw new Error('Selected role option is missing')
    }
    if (selectedLabels.length <= 2) {
      return selectedLabels.join('、')
    }

    return `役職: ${selectedLabels.length}件選択`
  }

  isSorted(field: OperatorSortField, itemId: string | null): boolean {
    return this.sort.field === field && this.sort.itemId === itemId
  }

  sortIndicator(field: OperatorSortField, itemId: string | null): string {
    if (!this.isSorted(field, itemId)) {
      return '↕'
    }

    return this.sort.direction === 'asc' ? '↑' : '↓'
  }

  ariaSort(
    field: OperatorSortField,
    itemId: string | null,
  ): 'ascending' | 'descending' | 'none' {
    if (!this.isSorted(field, itemId)) {
      return 'none'
    }

    return this.sort.direction === 'asc' ? 'ascending' : 'descending'
  }

  get columnCount(): number {
    if (this.dashboard == null) {
      return 5
    }

    return this.dashboard.items.length * 2 + 5
  }

  private loadMembers(): void {
    this.actionErrorMessage = ''
    if (!this.validatePeriodRange()) {
      return
    }

    const loadId = ++this.activeLoadId
    const requestedPeriodFrom = this.periodFrom
    const requestedPeriodTo = this.periodTo
    const requestedScoreCount = this.scoreCount
    this.isLoading = true
    this.loadErrorMessage = ''
    this.changeDetectorRef.markForCheck()

    forkJoin({
      dashboard: this.kpiDashboardService.getDashboard(this.dashboardId),
      members: this.kpiDashboardService.members(
        this.dashboardId,
        requestedPeriodFrom,
        requestedPeriodTo,
        requestedScoreCount,
      ),
      user: this.authService.user$.pipe(
        take(1),
        switchMap((authUser) => {
          if (authUser == null) {
            return throwError(() => new Error('Authenticated user is missing'))
          }
          return this.userStore.getOneSelf({ useCache: true })
        }),
      ),
    })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ({ dashboard, members, user }) => {
          if (loadId !== this.activeLoadId) {
            return
          }

          this.dashboard = {
            ...dashboard,
            items: [...dashboard.items].sort(
              (left, right) => left.sortOrder - right.sortOrder,
            ),
          }
          this.membersResult = this.applyExclusionOverrides(members)
          this.canOpenSettings = hasManagementRole(user)
          this.settledExclusionValuesDuringLoad = new Map()
          this.appliedPeriodFrom = requestedPeriodFrom
          this.appliedPeriodTo = requestedPeriodTo
          this.appliedScoreCount = requestedScoreCount
          this.roleOptions = buildRoleOptions(this.membersResult.members)
          this.reportToOptions = this.membersResult.reportToOptions
          this.isLoading = false
          const reload = this.restoreQuery()
          this.pruneSparklineCache(this.membersResult.members)
          this.recalculateView()
          if (reload) this.loadMembers()
          this.changeDetectorRef.markForCheck()
        },
        error: (error: unknown) => {
          this.handleLoadError(loadId, error)
        },
      })
  }

  private handleLoadError(loadId: number, error: unknown): void {
    if (loadId !== this.activeLoadId) {
      return
    }

    if (handleDashboardNotFound(error, this.router, this.snackBar)) {
      this.isLoading = false
      this.changeDetectorRef.markForCheck()
      return
    }
    captureException(error)
    this.settledExclusionValuesDuringLoad = new Map()
    this.isLoading = false
    this.loadErrorMessage = 'オペレーター一覧の読み込みに失敗しました。'
    // 取得中に届いた URL は、失敗時も復元する。再取得する場合は選択肢の検証を待つ。
    const reload = this.restoreQuery()
    this.recalculateView()
    if (reload) this.loadMembers()
    this.changeDetectorRef.markForCheck()
  }

  private recalculateView(): void {
    if (this.dashboard == null || this.membersResult == null) {
      return
    }

    const filter = this.currentFilter()
    this.totalMemberCount = this.membersResult.members.length
    const filteredMembers = filterMembers(this.membersResult.members, filter)
    const sortedMembers = sortMembers(filteredMembers, this.sort)
    const itemIds = this.dashboard.items.map((item) => item.id)
    this.filtersApplied = isFilterApplied(filter)
    this.displayedRows = sortedMembers.map((member) =>
      this.buildRowViewModel(member, itemIds),
    )
    const aggregate = this.filtersApplied
      ? aggregateMembers(sortedMembers, itemIds)
      : this.membersResult.all
    const metricCells = buildMetricCellViewModels(
      aggregate.metrics,
      itemIds,
      this.membersResult.all.orgTargets,
    )
    const periodMonths = countPeriodMonths(
      this.appliedPeriodFrom,
      this.appliedPeriodTo,
    )
    this.summary = {
      metricCells,
      orgTargetNote: buildOrgTargetNote(metricCells, periodMonths),
      averageScoreText: formatScore(aggregate.averageScore),
    }
  }

  private buildRowViewModel(
    member: KpiDashboardMember,
    itemIds: readonly string[],
  ): OperatorRowViewModel {
    const latestScore = member.scores.length === 0 ? null : member.scores[0]

    return {
      member,
      ...responseStatusDisplay(member.latestSheetResponseStatus),
      roleLabel: member.roleTitle == null ? '未設定' : member.roleTitle,
      metricCells: buildMetricCellViewModels(member.metrics, itemIds),
      latestScoreText: formatScore(latestScore),
      sparklineOptions: this.getSparklineOptions(member),
      exclusionPending: this.pendingExclusionUserIds.has(member.userId),
    }
  }

  private getSparklineOptions(
    member: KpiDashboardMember,
  ): Highcharts.Options | null {
    if (member.scores.length === 0) {
      return null
    }

    const cached = this.sparklineCache.get(member.userId)
    if (cached != null && areScoresEqual(cached.scores, member.scores)) {
      return cached.options
    }

    const options = createSparklineOptions(member.scores)
    this.sparklineCache.set(member.userId, {
      scores: [...member.scores],
      options,
    })
    return options
  }

  private pruneSparklineCache(members: readonly KpiDashboardMember[]): void {
    const userIds = new Set(members.map((member) => member.userId))
    for (const userId of this.sparklineCache.keys()) {
      if (!userIds.has(userId)) {
        this.sparklineCache.delete(userId)
      }
    }
  }

  private currentFilter(): OperatorListFilter {
    return {
      name: this.queryName,
      roleKeys: this.allRolesSelected() ? null : this.selectedRoleKeys,
      metricStatuses: this.metricStatuses,
      metricLogic: this.metricLogic,
      coaching: this.coachingFilter,
      reportToUserId: this.reportToUserId,
      response: this.notAnsweredOnly ? 'notAnswered' : 'all',
    }
  }

  private validatePeriodRange(): boolean {
    this.periodValidationMessage = validatePeriodRange(
      this.periodFrom,
      this.periodTo,
    )
    if (this.periodValidationMessage !== '') return false
    this.validPeriodFrom = this.periodFrom
    this.validPeriodTo = this.periodTo
    return true
  }

  private updateMemberExclusion(userId: string, excluded: boolean): void {
    if (this.membersResult == null) {
      throw new Error('Member result is missing')
    }

    this.membersResult = {
      ...this.membersResult,
      members: this.membersResult.members.map((member) =>
        member.userId === userId
          ? { ...member, excludedFromCoaching: excluded }
          : member,
      ),
    }
    this.recalculateView()
  }

  private applyExclusionOverrides(
    members: KpiDashboardMembers,
  ): KpiDashboardMembers {
    if (
      this.pendingExclusionValues.size === 0 &&
      this.settledExclusionValuesDuringLoad.size === 0
    ) {
      return members
    }

    return {
      ...members,
      members: members.members.map((member) => {
        const excluded = this.pendingExclusionValues.has(member.userId)
          ? this.pendingExclusionValues.get(member.userId)
          : this.settledExclusionValuesDuringLoad.get(member.userId)
        return typeof excluded === 'undefined'
          ? member
          : { ...member, excludedFromCoaching: excluded }
      }),
    }
  }

  private handleExclusionError(
    member: KpiDashboardMember,
    previousValue: boolean,
    event: MatCheckboxChange,
    error: unknown,
  ): void {
    captureException(error)
    event.source.checked = previousValue
    this.pendingExclusionUserIds = withoutUserId(
      this.pendingExclusionUserIds,
      member.userId,
    )
    this.pendingExclusionValues = withoutMapKey(
      this.pendingExclusionValues,
      member.userId,
    )
    this.updateMemberExclusion(member.userId, previousValue)
    this.actionErrorMessage = `${member.userName}さんの育成設定を更新できませんでした。`
    this.changeDetectorRef.markForCheck()
  }
}

function buildRoleOptions(
  members: readonly KpiDashboardMember[],
): RoleOption[] {
  const keys = [...new Set(members.map(getMemberRoleKey))].sort(
    (left, right) => {
      if (left === UNSET_ROLE_KEY) {
        return 1
      }
      if (right === UNSET_ROLE_KEY) {
        return -1
      }

      return left.localeCompare(right, 'ja')
    },
  )

  return keys.map((key) => ({
    key,
    label: key === UNSET_ROLE_KEY ? '未設定' : key,
  }))
}

function createSparklineOptions(
  scoresNewestFirst: readonly number[],
): Highcharts.Options {
  const scoresOldestFirst = [...scoresNewestFirst].reverse()
  const minimum = Math.min(...scoresOldestFirst)
  const maximum = Math.max(...scoresOldestFirst)
  const padding =
    minimum === maximum ? 3 : Math.max((maximum - minimum) * 0.2, 1)

  return {
    accessibility: { enabled: false },
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      height: 29,
      margin: [2, 2, 2, 2],
      reflow: false,
      type: 'line',
      width: 84,
    },
    credits: { enabled: false },
    legend: { enabled: false },
    plotOptions: {
      series: {
        animation: false,
        enableMouseTracking: false,
        lineWidth: 2,
        marker: {
          enabled: true,
          fillColor: '#ffffff',
          lineColor: '#2563eb',
          lineWidth: 1.5,
          radius: 2,
        },
        states: { inactive: { enabled: false }, hover: { enabled: false } },
      },
    },
    series: [
      {
        color: '#2563eb',
        data: scoresOldestFirst,
        type: 'line',
      },
    ],
    title: { text: undefined },
    tooltip: { enabled: false },
    xAxis: { visible: false },
    yAxis: {
      max: maximum + padding,
      min: minimum - padding,
      title: { text: undefined },
      visible: false,
    },
  }
}

function withoutUserId(
  userIds: ReadonlySet<string>,
  userId: string,
): Set<string> {
  return new Set([...userIds].filter((candidate) => candidate !== userId))
}

function withoutMapKey<T>(
  values: ReadonlyMap<string, T>,
  key: string,
): Map<string, T> {
  return new Map([...values].filter(([candidate]) => candidate !== key))
}

function areScoresEqual(
  left: readonly number[],
  right: readonly number[],
): boolean {
  return (
    left.length === right.length &&
    left.every((score, index) => score === right[index])
  )
}

function currentPeriod(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function isPeriod(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value)
}

function parsePeriod(value: string): { year: number; month: number } {
  if (!isPeriod(value)) {
    throw new Error(`Invalid period: ${value}`)
  }

  const [year, month] = value.split('-').map(Number)
  return { year, month }
}

function periodFromYearAndMonth(year: number, month: number): string {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    month < 1 ||
    month > 12
  ) {
    throw new Error(`Invalid year and month: ${year}-${month}`)
  }

  return `${year}-${String(month).padStart(2, '0')}`
}

function formatPeriod(period: string): string {
  const { year, month } = parsePeriod(period)
  return `${year}年${month}月`
}
